import { __awaiter, __rest } from "tslib";
import * as React from 'react';
import { useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, Stack, Text } from '@fluentui/react';
import PendingEventsList from './PendingEventsList';
import RejectReasonDialog from './RejectReasonDialog';
import { buildApprovalRequest, buildApprovalRequests, summarizeApprovalResponse } from './approvals';
/**
 * FM OOF approval workflow (F4) — composes the pending-events list with a reject-reason
 * dialog and runs approve/reject via `manage` (ManageApprovalRequests, PATCH), then surfaces
 * a transaction summary. Successfully processed items are removed from the visible pending list.
 */
export const PendingEventsApproval = (_a) => {
    var { manage } = _a, listProps = __rest(_a, ["manage"]);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectBulk, setRejectBulk] = useState(false);
    const [selected, setSelected] = useState([]);
    const [resolvedIds, setResolvedIds] = useState([]);
    const [message, setMessage] = useState(null);
    const clearSelection = () => setSelected([]);
    const toggleSelect = (item) => {
        setSelected((prev) => prev.some((s) => s.Id === item.Id)
            ? prev.filter((s) => s.Id !== item.Id)
            : [...prev, item]);
    };
    const run = (requests, verb) => __awaiter(void 0, void 0, void 0, function* () {
        setMessage(null);
        try {
            const resp = yield manage(requests);
            const s = summarizeApprovalResponse(resp);
            const succeededIds = resp
                .filter((item) => item.IsSuccess)
                .map((item) => item.RequestId);
            if (succeededIds.length > 0) {
                setResolvedIds((current) => Array.from(new Set([...current, ...succeededIds])));
            }
            if (s.allSucceeded) {
                setMessage({
                    type: MessageBarType.success,
                    text: `Event(s) successfully ${verb} (${s.succeeded}).`,
                });
            }
            else {
                setMessage({
                    type: MessageBarType.error,
                    text: `${s.succeeded} ${verb}, ${s.failed} failed.`,
                });
            }
        }
        catch (_b) {
            setMessage({
                type: MessageBarType.error,
                text: `Error ${verb === 'approved' ? 'approving' : 'rejecting'} event(s). Please try later.`,
            });
        }
    });
    const handleApprove = (item) => {
        void run([buildApprovalRequest(item, { isApproved: true })], 'approved');
    };
    const handleReject = (item) => {
        setRejectBulk(false);
        setRejectTarget(item);
    };
    const handleBulkApprove = () => {
        if (selected.length === 0) {
            return;
        }
        void run(buildApprovalRequests(selected, { isApproved: true }), 'approved');
        clearSelection();
    };
    const handleBulkReject = () => {
        if (selected.length === 0) {
            return;
        }
        setRejectTarget(null);
        setRejectBulk(true);
    };
    const closeDialog = () => {
        setRejectTarget(null);
        setRejectBulk(false);
    };
    const confirmReject = (reason) => {
        if (rejectBulk) {
            void run(buildApprovalRequests(selected, { isApproved: false, declineComments: reason }), 'rejected');
            clearSelection();
        }
        else if (rejectTarget) {
            void run([buildApprovalRequest(rejectTarget, { isApproved: false, declineComments: reason })], 'rejected');
        }
        closeDialog();
    };
    const dialogOpen = !!rejectTarget || rejectBulk;
    const dialogLabel = rejectBulk
        ? `${selected.length} selected event(s)`
        : rejectTarget
            ? `${rejectTarget.EventTypeName} for ${rejectTarget.ResourceId}`
            : undefined;
    const visibleMyItems = listProps.myItems.filter((item) => resolvedIds.indexOf(item.Id) === -1);
    const visibleDelegatedItems = (listProps.delegatedItems || []).filter((item) => resolvedIds.indexOf(item.Id) === -1);
    return (React.createElement(Stack, { tokens: { childrenGap: 12 }, "data-testid": "pending-events-approval" },
        message ? (React.createElement(MessageBar, { messageBarType: message.type, "data-testid": "pea-message" }, message.text)) : null,
        selected.length > 0 ? (React.createElement(Stack, { horizontal: true, verticalAlign: "center", wrap: true, tokens: { childrenGap: 8 }, "data-testid": "pea-bulk-bar", styles: { root: { padding: 8, background: '#eff6fc', borderRadius: 4 } } },
            React.createElement(Text, { styles: { root: { fontWeight: 600 } } },
                selected.length,
                " selected"),
            React.createElement(PrimaryButton, { text: `Approve selected (${selected.length})`, onClick: handleBulkApprove }),
            React.createElement(DefaultButton, { text: `Reject selected (${selected.length})`, onClick: handleBulkReject }),
            React.createElement(DefaultButton, { text: "Clear", onClick: clearSelection }))) : null,
        React.createElement(PendingEventsList, Object.assign({}, listProps, { myItems: visibleMyItems, delegatedItems: visibleDelegatedItems, selectable: true, selectedIds: selected.map((s) => s.Id), onToggleSelect: toggleSelect, onApprove: handleApprove, onReject: handleReject })),
        React.createElement(RejectReasonDialog, { hidden: !dialogOpen, eventLabel: dialogLabel, onConfirm: confirmReject, onDismiss: closeDialog })));
};
export default PendingEventsApproval;
//# sourceMappingURL=PendingEventsApproval.js.map